import { Injectable, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { LoggerService } from './logger.service';

export interface BrowserDebugInfo {
  deviceType: 'phone' | 'tablet' | 'pc' | null;
  isMobile: boolean | null;
  browserName: string | null;
  browserVersion: string | null;
}

/**
 * Browser Handler Service
 * Handles browser and device detection, sets data attributes on body element
 * Provides debug information via touch gesture (7+ touches)
 */
@Injectable({
  providedIn: 'root'
})
export class BrowserHandlerService implements OnDestroy {
  private documentRef = inject<Document>(DOCUMENT);

  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);
  private readonly body: HTMLBodyElement | null = null;
  private readonly userAgent: string = '';

  private debugInfos: BrowserDebugInfo = {
    deviceType: null,
    isMobile: null,
    browserName: null,
    browserVersion: null
  };

  private clickCount = 0;
  private readonly clickThreshold = 15;
  private touchTimeout?: ReturnType<typeof setTimeout>;
  private orientationChangeListener?: () => void;
  private touchStartListener?: () => void;
  private pwaSwipeBackHandler?: (e: TouchEvent) => void;
  private originalBodyStyle: string = '';
  private initialized = false;

  /** Left edge zone (px) where native iOS swipe-back gesture starts */
  private readonly SWIPE_BACK_EDGE_ZONE = 30;

  constructor() {
    const documentRef = this.documentRef;

    if (isPlatformBrowser(this.platformId) && documentRef.body) {
      Object.assign(this, {
        body: documentRef.body as HTMLBodyElement,
        userAgent: navigator.userAgent
      });
      this.originalBodyStyle = this.body!.getAttribute('style') || '';
    } else if (isPlatformBrowser(this.platformId)) {
      this.logger.error('BrowserHandlerService', new Error('Document body is not available'));
    }
  }

  /**
   * Initialize browser handler
   * Sets data attributes on body element and enables debug menu
   * Only runs in browser environment
   */
  init(): void {
    if (this.initialized) return;
    if (!isPlatformBrowser(this.platformId)) {
      this.logger.warn('BrowserHandlerService.init() called in non-browser environment');
      return;
    }

    if (!this.body) {
      this.logger.error('BrowserHandlerService', new Error('Body element not available'));
      return;
    }

    try {
      this.body.setAttribute('data-mobile', String(this.mobileDetection()));
      this.body.setAttribute('data-browser', this.browserDetection());
      this.body.setAttribute('data-browser-version', this.getBrowserVersion());
      this.body.setAttribute('data-device-type', this.deviceDetection());
      this.body.setAttribute('data-device-orientation', screen.orientation?.type || 'portrait-primary');
      this.body.setAttribute('data-pwa-installed', String(this.isInstalledPWA()));
      this.body.setAttribute('data-native-ios', String(this.isNativeIOSApp()));

      this.setupOrientationListener();
      this.setupDebugMenu();
      this.setupWebSwipeBackPrevention();
      this.setupIonicSwipeBackForNativeOnly();

      this.initialized = true;
      this.logger.log('BrowserHandlerService initialized', this.debugInfos);
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'init' });
    }
  }

  /**
   * Check and update device orientation
   */
  checkOrientation(): string {
    if (!isPlatformBrowser(this.platformId) || !this.body) {
      return 'portrait-primary';
    }

    try {
      const orientation = screen.orientation?.type || 'portrait-primary';
      this.body.setAttribute('data-device-orientation', orientation);
      return orientation;
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'checkOrientation' });
      return 'portrait-primary';
    }
  }

  /**
   * Detect device type (phone, tablet, or pc)
   */
  deviceDetection(): 'phone' | 'tablet' | 'pc' {
    const width = window.innerWidth;

    if (/mobile|android|iphone|ipod|blackberry|windows phone|iemobile/i.test(this.userAgent) && width <= 768) {
      this.debugInfos.deviceType = 'phone';
      return 'phone';
    } else if (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) {
      this.debugInfos.deviceType = 'tablet';
      return 'tablet';
    } else {
      this.debugInfos.deviceType = 'pc';
      return 'pc';
    }
  }

  /**
   * Detect if device is mobile
   */
  mobileDetection(): boolean {
    const mobileKeywords = ['Android', 'iPhone', 'iPad', 'Windows Phone', 'Mobile'];
    const isMobile = mobileKeywords.some(keyword => this.userAgent.includes(keyword));
    
    this.debugInfos.isMobile = isMobile;
    return isMobile;
  }

  /**
   * Detect browser name
   */
  browserDetection(): string {
    let browserName = '';

    // Check Edge first (Edge includes Chrome in user agent)
    if (this.userAgent.includes('Edg/') || this.userAgent.includes('EdgA/') || this.userAgent.includes('EdgiOS/')) {
      browserName = 'Microsoft Edge';
    } else if (this.userAgent.includes('Chrome')) {
      if (this.userAgent.includes('SamsungBrowser')) {
        browserName = 'Samsung Chromium';
      } else if (this.userAgent.includes('MiuiBrowser')) {
        browserName = 'Miui Chromium';
      } else {
        browserName = 'Google Chromium';
      }
    } else if (this.userAgent.includes('Safari') && 
               !this.userAgent.includes('Chrome') && 
               !this.userAgent.includes('CriOS')) {
      browserName = 'Apple Safari';
    } else if (this.userAgent.includes('CriOS')) {
      browserName = 'iOS Chrome';
    } else if (this.userAgent.includes('Firefox')) {
      browserName = 'Mozilla Firefox';
    } else if (this.userAgent.includes('MSIE') || this.userAgent.includes('Trident/')) {
      browserName = 'Internet Explorer';
    } else {
      browserName = 'Unbekannter Browser';
    }

    this.debugInfos.browserName = browserName;
    return browserName;
  }

  /**
   * Listen to dark mode preference changes
   * @param callback Function to call when preference changes
   */
  displayDetection(callback: (isDark: boolean) => void): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!window.matchMedia) {
      this.logger.warn('BrowserHandlerService: matchMedia not supported');
      return;
    }

    try {
      const query = window.matchMedia('(prefers-color-scheme: dark)');
      callback(query.matches);

      // Use addEventListener for better browser support
      if (query.addEventListener) {
        query.addEventListener('change', (event: MediaQueryListEvent) => callback(event.matches));
      } else {
        // Fallback for older browsers - addListener uses MediaQueryList as this context
        const handler = () => callback(query.matches);
        (query as any).addListener(handler);
      }
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'displayDetection' });
    }
  }

  /**
   * Get browser version from user agent
   */
  getBrowserVersion(): string {
    const ua = this.userAgent;
    let version = '';

    // Internet Explorer
    if (ua.indexOf('MSIE') !== -1 || ua.indexOf('Trident') !== -1) {
      const reIE = /MSIE (\d+\.\d+);/;
      const match = ua.match(reIE);
      if (match) {
        version = parseFloat(match[1]).toString();
      }
    }
    // Firefox
    else if (ua.indexOf('Firefox') !== -1) {
      const match = ua.match(/Firefox\/([0-9.]+)/);
      if (match) {
        version = match[1];
      }
    }
    // Chrome
    else if (ua.indexOf('Chrome') !== -1) {
      const match = ua.match(/Chrome\/([0-9.]+)/);
      if (match) {
        version = match[1];
      }
    }
    // Safari
    else if (ua.indexOf('Safari') !== -1) {
      const match = ua.match(/Version\/([0-9.]+)/);
      if (match) {
        version = match[1];
      }
    }
    // Opera
    else if (ua.indexOf('Opera') !== -1) {
      const match = ua.match(/Opera\/([0-9.]+)/);
      if (match) {
        version = match[1];
      }
    }

    this.debugInfos.browserVersion = version;
    return version;
  }

  /**
   * Check if running on iOS (including iPadOS 13+).
   * Used for PWA-specific behavior (install prompts, update checks).
   */
  isIOS(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const ua = navigator.userAgent;
    const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
    const hasIOSVersion = /os\s+(\d+)[_\s](\d+)/i.test(ua);
    const isSafari = /^((?!chrome|android|fxios|crios|edgios).)*safari/i.test(ua);
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const hasStandaloneProperty = 'standalone' in window.navigator;
    const isAppleVendor = /apple/i.test(navigator.vendor);

    return (
      (isIOSDevice && isSafari) ||
      (isIPadOS && hasStandaloneProperty) ||
      (hasIOSVersion && isSafari && isAppleVendor)
    );
  }

  /**
   * Check if the app is running as an installed PWA (standalone or fullscreen display mode).
   */
  isInstalledPWA(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;

    return isStandalone || isIOSStandalone || isFullscreen;
  }

  /**
   * Check if the app is running as a compiled native iOS app (Capacitor).
   * Ionic swipe-back is only enabled in this case.
   */
  isNativeIOSApp(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return Capacitor.getPlatform() === 'ios';
  }

  /**
   * Get current debug information
   */
  getDebugInfo(): BrowserDebugInfo {
    return { ...this.debugInfos };
  }

  /**
   * Setup orientation change listener
   */
  private setupOrientationListener(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      this.orientationChangeListener = () => {
        this.checkOrientation();
      };
      window.addEventListener('orientationchange', this.orientationChangeListener);
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'setupOrientationListener' });
    }
  }

  /**
   * Setup debug menu triggered by multiple touches
   */
  private setupDebugMenu(): void {
    if (!isPlatformBrowser(this.platformId) || !this.body) {
      return;
    }

    try {
      const debugInfoArray = ['--DEBUG-INFO--'];

      for (const key in this.debugInfos) {
        const value = this.debugInfos[key as keyof BrowserDebugInfo];
        debugInfoArray.push(`\n${key}: ${value}`);
      }

      this.touchStartListener = () => {
        if (!this.body) return;

        this.clickCount++;

        if (this.clickCount >= 7) {
          const blurValue = 0.5 * this.clickCount;
          const opacityValue = 0.5;
          const brightnessValue = 0.25 * this.clickCount;
          this.body.setAttribute(
            'style',
            `filter: blur(${blurValue}px) opacity(${opacityValue}) brightness(${brightnessValue}) saturate(0); ${this.originalBodyStyle}`
          );
        }

        if (this.clickCount >= this.clickThreshold) {
          alert(debugInfoArray.join(''));
          this.resetClickCount();
        }

        if (this.touchTimeout) {
          clearTimeout(this.touchTimeout);
        }

        this.touchTimeout = setTimeout(() => {
          this.resetClickCount();
        }, 250);
      };

      if (this.body) {
        this.body.addEventListener('touchstart', this.touchStartListener);
      }
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'setupDebugMenu' });
    }
  }

  /**
   * When running in browser or PWA: Block native swipe-back gesture from left edge
   * to prevent conflict with in-app navigation. Only in native iOS app this is not needed.
   */
  private setupWebSwipeBackPrevention(): void {
    if (!isPlatformBrowser(this.platformId) || this.isNativeIOSApp()) return;

    try {
      this.pwaSwipeBackHandler = (e: TouchEvent) => {
        const touch = e.touches[0] ?? e.changedTouches[0];
        if (!touch) return;
        const x = touch.clientX ?? touch.pageX;
        if (x < this.SWIPE_BACK_EDGE_ZONE) {
          e.preventDefault();
        }
      };

      this.documentRef.addEventListener('touchstart', this.pwaSwipeBackHandler, {
        passive: false,
        capture: true
      });

      this.logger.log('BrowserHandlerService: Web swipe-back prevention enabled');
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'setupWebSwipeBackPrevention' });
    }
  }

  /**
   * Disable Ionic swipe-back when NOT running as native iOS app.
   * Only the compiled iOS app should have Ionic's swipe-back gesture.
   */
  private setupIonicSwipeBackForNativeOnly(): void {
    if (!isPlatformBrowser(this.platformId) || this.isNativeIOSApp()) return;

    try {
      setTimeout(() => {
        const ionApp = this.documentRef.querySelector('ion-app');
        if (ionApp) {
          ionApp.style.setProperty('--swipe-back-enabled', 'false');
          this.logger.log('BrowserHandlerService: Ionic swipe-back disabled (web/PWA)');
        }
      }, 0);
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'setupIonicSwipeBackForNativeOnly' });
    }
  }

  /**
   * Reset click count and restore body style
   */
  private resetClickCount(): void {
    if (!isPlatformBrowser(this.platformId) || !this.body) {
      return;
    }

    try {
      this.clickCount = 0;
      this.body.setAttribute('style', this.originalBodyStyle);
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'resetClickCount' });
    }
  }

  /**
   * Cleanup listeners on service destruction
   */
  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (this.orientationChangeListener) {
        window.removeEventListener('orientationchange', this.orientationChangeListener);
      }
      if (this.touchStartListener && this.body) {
        this.body.removeEventListener('touchstart', this.touchStartListener);
      }
      if (this.pwaSwipeBackHandler) {
        this.documentRef.removeEventListener('touchstart', this.pwaSwipeBackHandler, { capture: true });
      }
      if (this.touchTimeout) {
        clearTimeout(this.touchTimeout);
      }
    } catch (error) {
      this.logger.error('BrowserHandlerService', error, { operation: 'ngOnDestroy' });
    }
  }
}
